/**
 * PLATYPUSS STREETWEAR // THEME JAVASCRIPT
 * Handles Cart Drawer, Ajax Cart API, Variant Switching, Image Gallery, Size Selector
 */

(function () {
  'use strict';

  // Dynamic Configuration Helpers
  function getFreeShippingThreshold() {
    if (window.PLATYPUSS_CONFIG && typeof window.PLATYPUSS_CONFIG.freeShippingThreshold === 'number') {
      return window.PLATYPUSS_CONFIG.freeShippingThreshold;
    }
    const meterEl = document.getElementById('platypussShippingMeter');
    if (meterEl && meterEl.dataset.threshold) {
      return parseInt(meterEl.dataset.threshold, 10);
    }
    return 99900; // default ₹999 in paise
  }

  function getPrimaryDiscountCode() {
    return (window.PLATYPUSS_CONFIG && window.PLATYPUSS_CONFIG.primaryDiscountCode) || 'Welcome10';
  }

  // Utility: Format INR Currency
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    const rupees = Math.round(cents / 100);
    return '₹' + rupees.toLocaleString('en-IN');
  }

  // Modern Toast Notification Controller
  window.PlatypussToast = {
    toastEl: null,
    timer: null,

    init() {
      this.toastEl = document.getElementById('platypussToast');
    },

    show(message, icon = 'info', actionText = null, actionCallback = null) {
      if (!this.toastEl) this.toastEl = document.getElementById('platypussToast');
      if (!this.toastEl) return;

      clearTimeout(this.timer);

      let actionHtml = '';
      if (actionText) {
        actionHtml = `<button type="button" class="platypuss-toast-action" style="background: var(--platypuss-primary); color: var(--platypuss-on-primary); border: none; border-radius: var(--radius-sm); padding: 5px 12px; font-family: var(--font-mono); font-size: 11px; font-weight: 700; cursor: pointer; text-transform: uppercase; letter-spacing: 0.05em; transition: transform 0.2s var(--spring-snappy); margin-left: auto;">${actionText}</button>`;
      }

      this.toastEl.innerHTML = `
        <span class="material-symbols-outlined" style="color: var(--platypuss-primary); font-size: 20px;">${icon}</span>
        <span style="font-family: var(--font-mono); font-size: 12px; letter-spacing: 0.04em;">${message}</span>
        ${actionHtml}
      `;

      if (actionText && actionCallback) {
        const btn = this.toastEl.querySelector('.platypuss-toast-action');
        if (btn) {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.hide();
            actionCallback();
          }, { once: true });
        }
      }

      this.toastEl.classList.add('active');

      this.timer = setTimeout(() => {
        this.hide();
      }, 3500);
    },

    hide() {
      if (this.toastEl) {
        this.toastEl.classList.remove('active');
      }
    }
  };

  // Cart Drawer Controller
  window.PlatypussCart = {
    drawer: null,
    overlay: null,
    currentCart: null,
    itemMap: {},

    init() {
      this.drawer = document.getElementById('platypussCartDrawer');
      this.overlay = document.getElementById('platypussCartOverlay');

      // Event listeners for open / close
      document.addEventListener('click', (e) => {
        const openTrigger = e.target.closest('[data-cart-drawer-trigger]');
        if (openTrigger) {
          e.preventDefault();
          this.open();
        }

        const closeTrigger = e.target.closest('[data-cart-drawer-close]');
        if (closeTrigger) {
          const href = closeTrigger.getAttribute('href');
          if (closeTrigger.tagName === 'A' && href && href !== '#' && !href.startsWith('javascript:')) {
            this.close();
            return;
          }
          e.preventDefault();
          this.close();
        }
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Cart Item Steppers & Remove inside Cart Drawer
      if (this.drawer) {
        this.drawer.addEventListener('click', (e) => {
          const plusBtn = e.target.closest('[data-cart-qty-plus]');
          if (plusBtn) {
            const itemKey = plusBtn.dataset.itemKey;
            const currentQty = parseInt(plusBtn.dataset.qty, 10);
            const maxQty = this.getMaxQty();
            if (currentQty >= maxQty) {
              alert(`Maximum limit reached (${maxQty} units per item).`);
              return;
            }
            this.updateQuantity(itemKey, Math.min(maxQty, currentQty + 1));
            return;
          }

          const minusBtn = e.target.closest('[data-cart-qty-minus]');
          if (minusBtn) {
            const itemKey = minusBtn.dataset.itemKey;
            const currentQty = parseInt(minusBtn.dataset.qty, 10);
            this.updateQuantity(itemKey, Math.max(0, currentQty - 1));
            return;
          }

          const removeBtn = e.target.closest('[data-cart-remove]');
          if (removeBtn) {
            const itemKey = removeBtn.dataset.itemKey;
            this.updateQuantity(itemKey, 0);
            return;
          }
        });
      }

      // Universal Card Quick Add & Card Stepper delegated click handling
      document.addEventListener('click', (e) => {
        const quickAddBtn = e.target.closest('[data-quick-add-btn]');
        if (quickAddBtn) {
          e.preventDefault();
          const card = quickAddBtn.closest('[data-product-card]');
          const selectedPill = card ? card.querySelector('.platypuss-size-pill.active') : null;
          const variantId = selectedPill ? selectedPill.dataset.variantId : quickAddBtn.dataset.defaultVariantId;

          if (!variantId) {
            alert('Please select a size first');
            return;
          }

          const originalText = quickAddBtn.innerHTML;
          quickAddBtn.innerHTML = '<span class="material-symbols-outlined text-[16px] animate-spin">sync</span> ADDING...';
          quickAddBtn.disabled = true;

          this.changeVariantQuantity(variantId, 1, () => {
            quickAddBtn.innerHTML = originalText;
            quickAddBtn.disabled = false;
          });
          return;
        }

        const cardStepperPlus = e.target.closest('[data-card-stepper-plus]');
        if (cardStepperPlus) {
          e.preventDefault();
          const card = cardStepperPlus.closest('[data-product-card]');
          const selectedPill = card ? card.querySelector('.platypuss-size-pill.active') : null;
          const variantId = selectedPill ? selectedPill.dataset.variantId : card?.querySelector('[data-quick-add-btn]')?.dataset.defaultVariantId;
          if (variantId) {
            cardStepperPlus.disabled = true;
            this.changeVariantQuantity(variantId, 1, () => {
              cardStepperPlus.disabled = false;
            });
          }
          return;
        }

        const cardStepperMinus = e.target.closest('[data-card-stepper-minus]');
        if (cardStepperMinus) {
          e.preventDefault();
          const card = cardStepperMinus.closest('[data-product-card]');
          const selectedPill = card ? card.querySelector('.platypuss-size-pill.active') : null;
          const variantId = selectedPill ? selectedPill.dataset.variantId : card?.querySelector('[data-quick-add-btn]')?.dataset.defaultVariantId;
          if (variantId) {
            cardStepperMinus.disabled = true;
            this.changeVariantQuantity(variantId, -1, () => {
              cardStepperMinus.disabled = false;
            });
          }
          return;
        }
      });

      // Initial Cart Sync
      this.refreshCart();
    },

    getMaxQty() {
      return (window.PLATYPUSS_CONFIG && window.PLATYPUSS_CONFIG.maxCartQtyPerItem) ? parseInt(window.PLATYPUSS_CONFIG.maxCartQtyPerItem, 10) : 50;
    },

    isOpen() {
      return this.drawer && this.drawer.classList.contains('active');
    },

    open() {
      if (this.drawer) this.drawer.classList.add('active');
      if (this.overlay) this.overlay.classList.add('active');
      document.body.classList.add('platypuss-cart-open');
      document.body.style.overflow = 'hidden';
      this.refreshCart();
    },

    close() {
      if (this.drawer) this.drawer.classList.remove('active');
      if (this.overlay) this.overlay.classList.remove('active');
      document.body.classList.remove('platypuss-cart-open');
      document.body.style.overflow = '';
    },

    rebuildItemMap(cart) {
      this.currentCart = cart;
      this.itemMap = {};
      if (cart && Array.isArray(cart.items)) {
        cart.items.forEach(item => {
          const vId = String(item.variant_id || item.id);
          if (!this.itemMap[vId]) {
            this.itemMap[vId] = {
              key: item.key,
              quantity: item.quantity,
              id: item.id,
              variant_id: item.variant_id
            };
          } else {
            this.itemMap[vId].quantity += item.quantity;
          }
        });
      }
    },

    getVariantQty(variantId) {
      if (!variantId) return 0;
      const item = this.itemMap[String(variantId)];
      return item ? item.quantity : 0;
    },

    getVariantItem(variantId) {
      if (!variantId) return null;
      return this.itemMap[String(variantId)] || null;
    },

    changeVariantQuantity(variantId, delta, callback) {
      if (!variantId) {
        if (callback) callback(null);
        return;
      }
      const vIdStr = String(variantId);
      const existingItem = this.getVariantItem(vIdStr);
      const currentQty = existingItem ? existingItem.quantity : 0;
      const newQty = currentQty + delta;
      const maxQty = this.getMaxQty();

      if (delta > 0 && newQty > maxQty) {
        alert(`Maximum quantity reached (${maxQty} units per item). You already have ${currentQty} in your cart.`);
        if (callback) callback(null);
        return;
      }

      if (newQty <= 0) {
        if (existingItem && existingItem.key) {
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: existingItem.key, quantity: 0 })
          })
            .then(async (res) => {
              const data = await res.json();
              if (!res.ok || (data.status && data.status >= 400)) {
                this.refreshCart();
                if (callback) callback(null);
                return;
              }
              this.renderCart(data);
              if (callback) callback(data);
            })
            .catch(err => {
              console.error('Error removing cart item:', err);
              if (callback) callback(null);
            });
        } else {
          this.refreshCart();
          if (callback) callback(null);
        }
        return;
      }

      if (existingItem && existingItem.key) {
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: existingItem.key, quantity: newQty })
        })
          .then(async (res) => {
            const data = await res.json();
            if (!res.ok || (data.status && data.status >= 400)) {
              const errMsg = data.description || data.message || 'Could not update item quantity.';
              alert(errMsg);
              this.refreshCart();
              if (callback) callback(null);
              return;
            }
            this.renderCart(data);
            if (delta > 0 && window.PlatypussToast) {
              window.PlatypussToast.show('PIECE SECURED IN VAULT', 'shopping_bag', 'VIEW VAULT', () => {
                if (window.PlatypussCart && window.PlatypussCart.open) window.PlatypussCart.open();
              });
            }
            if (callback) callback(data);
          })
          .catch(err => {
            console.error('Error changing cart quantity:', err);
            if (callback) callback(null);
          });
      } else {
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: newQty })
        })
          .then(async (res) => {
            const data = await res.json();
            if (!res.ok || (data.status && data.status >= 400)) {
              const errMsg = data.description || data.message || 'Could not add item to cart. It may be out of stock.';
              alert(errMsg);
              this.refreshCart();
              if (callback) callback(null);
              return;
            }
            this.refreshCart((cart) => {
              if (delta > 0 && window.PlatypussToast) {
                window.PlatypussToast.show('PIECE SECURED IN VAULT', 'shopping_bag', 'VIEW VAULT', () => {
                  if (window.PlatypussCart && window.PlatypussCart.open) window.PlatypussCart.open();
                });
              }
              if (callback) callback(cart || true);
            });
          })
          .catch(err => {
            console.error('Error adding variant to cart:', err);
            alert('Could not add item to cart. Please try again.');
            if (callback) callback(null);
          });
      }
    },

    addItem(variantId, quantity = 1, callback) {
      this.changeVariantQuantity(variantId, quantity, callback);
    },

    updateQuantity(itemKey, quantity) {
      const maxQty = this.getMaxQty();
      if (quantity > maxQty) {
        alert(`Maximum quantity reached (${maxQty} units per item).`);
        quantity = maxQty;
      }
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: quantity })
      })
        .then(async (res) => {
          const data = await res.json();
          if (!res.ok || (data.status && data.status >= 400)) {
            const errMsg = data.description || data.message || 'Could not update item quantity.';
            alert(errMsg);
            this.refreshCart();
            return;
          }
          this.renderCart(data);
        })
        .catch((err) => {
          console.error('Error changing cart quantity:', err);
        });
    },

    refreshCart(callback) {
      return fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => {
          this.renderCart(cart);
          if (callback) callback(cart);
          return cart;
        })
        .catch((err) => {
          console.error('Error fetching cart:', err);
          if (callback) callback(null);
        });
    },

    syncAllAtcButtons(cart) {
      this.syncPdpAtcButtons();
      this.syncCardAtcButtons();
    },

    syncPdpAtcButtons() {
      const hiddenInput = document.getElementById('pdpVariantId');
      if (!hiddenInput) return;
      const variantId = hiddenInput.value;
      const qty = this.getVariantQty(variantId);

      // Main PDP ATC Morphing
      const mainBtn = document.getElementById('pdpAddToCartBtn');
      const mainStepper = document.getElementById('pdpAtcStepper');
      const mainQtyEl = document.getElementById('pdpAtcStepperQty');
      const mainMinusIcon = mainStepper ? mainStepper.querySelector('.minus-icon') : null;

      if (mainBtn && mainStepper && mainQtyEl) {
        if (qty > 0) {
          mainBtn.style.display = 'none';
          mainStepper.style.display = 'flex';
          mainQtyEl.textContent = qty;
          if (mainMinusIcon) {
            mainMinusIcon.textContent = qty === 1 ? 'delete' : 'remove';
          }
        } else {
          mainBtn.style.display = 'inline-flex';
          mainStepper.style.display = 'none';
        }
      }

      // Mobile Sticky Bar ATC Morphing
      const stickyBtn = document.getElementById('pdpStickyAddToCartBtn');
      const stickyStepper = document.getElementById('pdpStickyAtcStepper');
      const stickyQtyEl = document.getElementById('pdpStickyStepperQty');
      const stickyMinusIcon = stickyStepper ? stickyStepper.querySelector('.minus-icon') : null;

      if (stickyBtn && stickyStepper && stickyQtyEl) {
        if (qty > 0) {
          stickyBtn.style.display = 'none';
          stickyStepper.style.display = 'flex';
          stickyQtyEl.textContent = qty;
          if (stickyMinusIcon) {
            stickyMinusIcon.textContent = qty === 1 ? 'delete' : 'remove';
          }
        } else {
          stickyBtn.style.display = 'inline-flex';
          stickyStepper.style.display = 'none';
        }
      }

      // Sync the optional PDP quantity input value if present
      const qtyInput = document.getElementById('pdpQuantityInput');
      if (qtyInput && qty > 0) {
        qtyInput.value = qty;
      }
    },

    syncCardAtc(card) {
      if (!card) return;
      const activePill = card.querySelector('.platypuss-size-pill.active');
      const quickAddBtn = card.querySelector('[data-quick-add-btn]');
      const variantId = activePill ? activePill.dataset.variantId : (quickAddBtn ? quickAddBtn.dataset.defaultVariantId : null);
      if (!variantId) return;

      const isAvailable = activePill ? (activePill.dataset.available !== 'false') : (!quickAddBtn.disabled);

      const qty = this.getVariantQty(variantId);
      const stepper = card.querySelector('[data-card-stepper]');
      const qtyEl = card.querySelector('[data-card-stepper-qty]');
      const minusIcon = stepper ? stepper.querySelector('.minus-icon') : null;

      if (quickAddBtn && stepper && qtyEl) {
        if (qty > 0) {
          quickAddBtn.style.display = 'none';
          stepper.style.display = 'flex';
          qtyEl.textContent = qty;
          if (minusIcon) {
            minusIcon.textContent = qty === 1 ? 'delete' : 'remove';
          }
        } else {
          quickAddBtn.style.display = 'inline-flex';
          stepper.style.display = 'none';

          const btnSpan = quickAddBtn.querySelector('span:last-child');
          if (!isAvailable) {
            quickAddBtn.disabled = true;
            quickAddBtn.style.opacity = '0.5';
            quickAddBtn.style.cursor = 'not-allowed';
            if (btnSpan) btnSpan.textContent = 'SOLD OUT';
          } else {
            quickAddBtn.disabled = false;
            quickAddBtn.style.opacity = '1';
            quickAddBtn.style.cursor = 'pointer';
            if (btnSpan) btnSpan.textContent = 'QUICK ADD +';
          }
        }
      }
    },

    syncCardAtcButtons() {
      const cards = document.querySelectorAll('[data-product-card]');
      cards.forEach(card => this.syncCardAtc(card));
    },

    renderCart(cart) {
      this.rebuildItemMap(cart);

      // Update global badges (Desktop header and Mobile floating dock)
      const countEls = document.querySelectorAll('[data-cart-count], [data-dock-cart-count]');
      countEls.forEach((el) => {
        const prev = el.textContent.trim();
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
        if (prev !== String(cart.item_count)) {
          el.classList.remove('pop');
          void el.offsetWidth;
          el.classList.add('pop');
        }
      });

      // Update Free Shipping Progress Bar
      const meterEl = document.getElementById('platypussShippingMeter');
      if (meterEl) {
        const threshold = getFreeShippingThreshold();
        const thresholdFormatted = formatMoney(threshold);
        const fillEl = document.getElementById('platypussShippingFill');
        const textEl = document.getElementById('platypussShippingText');
        const metricEl = document.getElementById('platypussShippingMetric');

        const remaining = threshold - cart.total_price;
        const progress = Math.min(100, Math.max(0, (cart.total_price / threshold) * 100));

        if (fillEl) fillEl.style.width = progress + '%';

        if (remaining <= 0) {
          meterEl.classList.add('unlocked');
          if (textEl) textEl.innerHTML = '<span class="platypuss-shimmer-text">⚡ UNLOCKED: <strong>FREE EXPRESS SHIPPING</strong></span>';
          if (metricEl) metricEl.innerHTML = '<span style="color: var(--platypuss-primary); font-weight: 900;">UNLOCKED ✓</span>';
        } else {
          meterEl.classList.remove('unlocked');
          if (textEl) textEl.innerHTML = `⚡ ADD <strong>${formatMoney(remaining)}</strong> FOR FREE SHIPPING`;
          if (metricEl) metricEl.textContent = `${Math.round(progress)}%`;
        }
      }

      // Render Items List
      const bodyEl = document.getElementById('platypussDrawerBody');
      const footerEl = document.getElementById('platypussDrawerFooter');

      if (!bodyEl) return;

      if (cart.item_count === 0) {
        bodyEl.innerHTML = `
          <div class="platypuss-cart-empty" style="text-align: center; padding: 60px 20px;">
            <div style="font-size: 48px; margin-bottom: 16px;">🥋</div>
            <h3 style="font-family: var(--font-display); font-size: 22px; color: var(--platypuss-tertiary); margin-bottom: 8px;">YOUR VAULT IS EMPTY</h3>
            <p style="color: var(--platypuss-text-muted); font-size: 14px; margin-bottom: 24px;">No streetwear pieces added yet. Discover our latest high-GSM drops.</p>
            <a href="/collections/all" class="platypuss-btn-primary" style="display: inline-flex;" data-cart-drawer-close>
              EXPLORE ALL DROPS →
            </a>
          </div>
        `;
        if (footerEl) footerEl.style.display = 'none';
        this.syncAllAtcButtons(cart);
        return;
      }

      if (footerEl) footerEl.style.display = '';

      let itemsHtml = '';
      const maxCartLimit = this.getMaxQty();

      cart.items.forEach((item) => {
        const itemPrice = formatMoney(item.final_line_price);
        const singlePrice = formatMoney(item.price);
        const optionsList = item.options_with_values ? item.options_with_values.map(o => `${o.name}: ${o.value}`).join(' • ') : '';
        const isAtMax = item.quantity >= maxCartLimit;
        let itemImgUrl = '';
        if (item.featured_image) {
          itemImgUrl = typeof item.featured_image === 'string' ? item.featured_image : (item.featured_image.url || item.featured_image.src || '');
        } else if (item.image) {
          itemImgUrl = typeof item.image === 'string' ? item.image : (item.image.src || item.image.url || '');
        }
        const safeTitle = (item.product_title || item.title || '').replace(/"/g, '&quot;');

        itemsHtml += `
          <div class="platypuss-cart-item" style="display: flex; gap: 16px; padding-bottom: 18px; border-bottom: 1px solid var(--platypuss-border); align-items: center;">
            <a href="${item.url}" style="width: 88px; height: 112px; flex-shrink: 0; border-radius: var(--radius-sm); overflow: hidden; background: var(--platypuss-surface-dim); border: 1px solid var(--platypuss-border); display: block;">
              ${itemImgUrl ? `<img src="${itemImgUrl}" alt="${safeTitle}" style="width: 100%; height: 100%; object-fit: cover;">` : `<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: var(--platypuss-text-muted);"><span class="material-symbols-outlined" style="font-size: 24px;">image</span></div>`}
            </a>
            <div style="flex: 1; display: flex; flex-direction: column; gap: 5px;">
              <a href="${item.url}" style="font-family: var(--font-display); font-size: 16.5px; font-weight: 700; color: var(--platypuss-tertiary); line-height: 1.25;">
                ${safeTitle}
              </a>
              ${optionsList ? `<div style="font-family: var(--font-body); font-size: 12.5px; font-weight: 700; text-transform: uppercase; color: var(--platypuss-primary);">${optionsList}</div>` : ''}
              <div style="display: flex; align-items: baseline; gap: 8px;">
                <span style="font-family: var(--font-body); font-size: 16px; font-weight: 900; color: var(--platypuss-tertiary);">
                  ${itemPrice}
                </span>
                ${item.quantity > 1 ? `<span style="font-size: 12px; color: var(--platypuss-text-muted);">(${singlePrice} each)</span>` : ''}
              </div>
              <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 6px;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <div class="platypuss-stepper" style="height: 38px;">
                    <button type="button" class="platypuss-stepper-btn" data-cart-qty-minus data-item-key="${item.key}" data-qty="${item.quantity}" style="width: 36px; height: 36px; font-size: 18px;" aria-label="Decrease quantity">-</button>
                    <span class="platypuss-stepper-val" style="font-size: 15px; font-weight: 900; min-width: 30px;">${item.quantity}</span>
                    <button type="button" class="platypuss-stepper-btn" data-cart-qty-plus data-item-key="${item.key}" data-qty="${item.quantity}" style="width: 36px; height: 36px; font-size: 18px; ${isAtMax ? 'opacity: 0.35; cursor: not-allowed;' : ''}" ${isAtMax ? 'title="Maximum limit reached"' : ''} aria-label="Increase quantity">+</button>
                  </div>
                  ${isAtMax ? `<span style="font-family: var(--font-body); font-size: 11px; font-weight: 800; color: var(--platypuss-primary); text-transform: uppercase;">MAX (${maxCartLimit})</span>` : ''}
                </div>
                <button type="button" class="platypuss-icon-btn" style="width: 38px; height: 38px;" data-cart-remove data-item-key="${item.key}" title="Remove item">
                  <span class="material-symbols-outlined" style="font-size: 18px;">delete</span>
                </button>
              </div>
            </div>
          </div>
        `;
      });

      bodyEl.innerHTML = itemsHtml;

      // Update Subtotal & Checkout links
      const subtotalEl = document.getElementById('platypussDrawerSubtotal');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

      // Synchronize all Add-to-Cart buttons and steppers across the site
      this.syncAllAtcButtons(cart);
    }
  };

  // Product Card Size Selector Handler
  function initProductCardPills() {
    document.addEventListener('click', (e) => {
      const pill = e.target.closest('.platypuss-size-pill');
      if (pill) {
        const container = pill.closest('.platypuss-size-pills');
        if (container) {
          container.querySelectorAll('.platypuss-size-pill').forEach(p => p.classList.remove('active'));
          pill.classList.add('active');

          const card = pill.closest('[data-product-card]');
          if (card) {
            const priceEl = card.querySelector('.platypuss-price');
            if (priceEl && pill.dataset.price) {
              priceEl.textContent = formatMoney(parseInt(pill.dataset.price, 10));
            }
            if (window.PlatypussCart && typeof window.PlatypussCart.syncCardAtc === 'function') {
              window.PlatypussCart.syncCardAtc(card);
            }
          }
        }
      }
    });
  }

  // PDP Interactive Controller
  function initPDP() {
    const pdpRoot = document.querySelector('[data-platypuss-pdp]');
    if (!pdpRoot) return;

    // Gallery Thumbnails
    const mainImg = pdpRoot.querySelector('#pdpMainImage');
    const thumbBtns = pdpRoot.querySelectorAll('[data-thumb-img]');

    thumbBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const newSrc = btn.dataset.thumbImg;
        if (mainImg && newSrc) {
          mainImg.src = newSrc;
          const zoomSrc = btn.dataset.zoomImg || newSrc;
          mainImg.setAttribute('data-zoom-src', zoomSrc);
          thumbBtns.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
        }
      });
    });

    // Dynamic Option Matching (Color, Size, etc.)
    const variantsJsonEl = pdpRoot.querySelector('#pdpVariantsJson');
    let variants = [];
    if (variantsJsonEl) {
      try {
        variants = JSON.parse(variantsJsonEl.textContent);
      } catch (e) {
        variants = [];
      }
    }

    const optionGroups = pdpRoot.querySelectorAll('.platypuss-option-group');

    function updateSelectedVariant() {
      if (!variants.length || !optionGroups.length) return;

      const selectedValues = [];
      optionGroups.forEach((group) => {
        const activeBtn = group.querySelector('.platypuss-opt-btn.active');
        const optIndex = parseInt(group.dataset.optionIndex || '0', 10);
        if (activeBtn) {
          selectedValues[optIndex] = activeBtn.dataset.optionValue;
        }
      });

      // Match in variants array
      const matched = variants.find((v) => {
        return selectedValues.every((val, idx) => v.options[idx] === val);
      });

      if (matched) {
        if (hiddenVariantInput) hiddenVariantInput.value = matched.id;
        if (priceDisplay && matched.price) priceDisplay.textContent = formatMoney(matched.price);

        const compareDisplay = pdpRoot.querySelector('.platypuss-price-compare');
        if (compareDisplay) {
          if (matched.compare_at_price && matched.compare_at_price > matched.price) {
            compareDisplay.textContent = formatMoney(matched.compare_at_price);
            compareDisplay.style.display = '';
          } else {
            compareDisplay.style.display = 'none';
          }
        }

        if (stockBadge) {
          stockBadge.textContent = matched.available ? 'In Stock' : 'Out of Stock';
          stockBadge.style.color = matched.available ? 'var(--platypuss-primary)' : 'var(--platypuss-error)';
        }

        if (atcBtn) {
          atcBtn.disabled = !matched.available;
          atcBtn.style.opacity = matched.available ? '1' : '0.5';
          atcBtn.style.cursor = matched.available ? 'pointer' : 'not-allowed';
          const btnText = atcBtn.querySelector('.btn-text');
          if (btnText) btnText.textContent = matched.available ? 'ADD TO CART' : 'SOLD OUT';
        }

        const directBuyBtn = document.getElementById('pdpDirectBuyBtn');
        if (directBuyBtn) {
          if (matched.available) {
            directBuyBtn.style.opacity = '1';
            directBuyBtn.style.pointerEvents = 'auto';
            directBuyBtn.style.cursor = 'pointer';
            directBuyBtn.removeAttribute('aria-disabled');
          } else {
            directBuyBtn.style.opacity = '0.5';
            directBuyBtn.style.pointerEvents = 'none';
            directBuyBtn.style.cursor = 'not-allowed';
            directBuyBtn.setAttribute('aria-disabled', 'true');
          }
        }

        // Sync Mobile Sticky Add-to-Cart Bar
        const stickyPrice = document.getElementById('pdpStickyLivePrice');
        if (stickyPrice) {
          stickyPrice.textContent = formatMoney(matched.price);
        }
        const stickyAtcBtn = document.getElementById('pdpStickyAddToCartBtn');
        if (stickyAtcBtn) {
          stickyAtcBtn.disabled = !matched.available;
          const stickyBtnSpan = stickyAtcBtn.querySelector('span');
          if (stickyBtnSpan) {
            stickyBtnSpan.textContent = matched.available ? 'ADD TO CART' : 'SOLD OUT';
          }
          stickyAtcBtn.style.opacity = matched.available ? '1' : '0.5';
          stickyAtcBtn.style.cursor = matched.available ? 'pointer' : 'not-allowed';
        }

        // Synchronize Stepper display for newly selected variant
        if (window.PlatypussCart && typeof window.PlatypussCart.syncPdpAtcButtons === 'function') {
          window.PlatypussCart.syncPdpAtcButtons();
        }

        const vImgSrc = (typeof matched.featured_image === 'string' ? matched.featured_image : (matched.featured_image && (matched.featured_image.src || matched.featured_image.url))) || '';
        if (vImgSrc && mainImg) {
          mainImg.src = vImgSrc;
          mainImg.setAttribute('data-zoom-src', vImgSrc);
        }

        // Live SKU sync across header and specification accordion
        const skuDisplays = pdpRoot.querySelectorAll('#pdpSku, #pdpSpecSku, [data-pdp-sku]');
        skuDisplays.forEach((el) => {
          el.textContent = matched.sku || 'PLTP-STREET-01';
        });

        // Live Stock status sync in specification accordion
        const specStock = pdpRoot.querySelector('#pdpSpecStock');
        if (specStock) {
          specStock.textContent = matched.available ? 'IN STOCK' : 'SOLD OUT';
          specStock.style.color = matched.available ? 'var(--platypuss-primary)' : 'var(--platypuss-error)';
        }

        // Live Weight sync in specification accordion if available
        const specWeight = pdpRoot.querySelector('#pdpSpecWeight');
        if (specWeight && matched.weight) {
          specWeight.textContent = `${matched.weight}g`;
        }

        // Sync browser URL with selected variant ID (?variant=123456)
        if (window.history && window.history.replaceState) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('variant', matched.id);
          window.history.replaceState({}, '', newUrl.toString());
        }
      } else {
        if (hiddenVariantInput) hiddenVariantInput.value = '';
        if (stockBadge) {
          stockBadge.textContent = 'Unavailable';
          stockBadge.style.color = 'var(--platypuss-error)';
        }
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.style.opacity = '0.5';
          atcBtn.style.cursor = 'not-allowed';
          const btnText = atcBtn.querySelector('.btn-text');
          if (btnText) btnText.textContent = 'UNAVAILABLE';
        }
        const directBuyBtn = document.getElementById('pdpDirectBuyBtn');
        if (directBuyBtn) {
          directBuyBtn.style.opacity = '0.5';
          directBuyBtn.style.pointerEvents = 'none';
          directBuyBtn.style.cursor = 'not-allowed';
          directBuyBtn.setAttribute('aria-disabled', 'true');
        }
        const stickyAtcBtn = document.getElementById('pdpStickyAddToCartBtn');
        if (stickyAtcBtn) {
          stickyAtcBtn.disabled = true;
          stickyAtcBtn.style.opacity = '0.5';
          stickyAtcBtn.style.cursor = 'not-allowed';
          const stickyBtnSpan = stickyAtcBtn.querySelector('span');
          if (stickyBtnSpan) stickyBtnSpan.textContent = 'UNAVAILABLE';
        }
      }
    }

    optionGroups.forEach((group) => {
      const btns = group.querySelectorAll('.platypuss-opt-btn');
      const labelVal = group.querySelector('.platypuss-selected-option-val');

      btns.forEach((btn) => {
        btn.addEventListener('click', () => {
          btns.forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
          if (labelVal) labelVal.textContent = btn.dataset.optionValue;
          updateSelectedVariant();
        });
      });
    });

    // Legacy Single-List Size Selector Support
    const sizeBtns = pdpRoot.querySelectorAll('[data-pdp-size-btn]');
    const hiddenVariantInput = pdpRoot.querySelector('#pdpVariantId');
    const atcBtn = pdpRoot.querySelector('#pdpAddToCartBtn');
    const priceDisplay = pdpRoot.querySelector('#pdpLivePrice');
    const stockBadge = pdpRoot.querySelector('#pdpStockBadge');

    sizeBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        sizeBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        const variantId = btn.dataset.variantId;
        const price = btn.dataset.price;
        const available = btn.dataset.available === 'true';

        if (hiddenVariantInput) hiddenVariantInput.value = variantId;
        if (priceDisplay && price) priceDisplay.textContent = formatMoney(parseInt(price, 10));

        if (stockBadge) {
          stockBadge.textContent = available ? 'In Stock' : 'Out of Stock';
          stockBadge.style.color = available ? 'var(--platypuss-primary)' : 'var(--platypuss-error)';
        }

        if (atcBtn) {
          atcBtn.disabled = !available;
          atcBtn.querySelector('.btn-text').textContent = available ? 'ADD TO CART' : 'SOLD OUT';
        }

        const directBuyBtn = document.getElementById('pdpDirectBuyBtn');
        if (directBuyBtn) {
          if (available) {
            directBuyBtn.style.opacity = '1';
            directBuyBtn.style.pointerEvents = 'auto';
            directBuyBtn.style.cursor = 'pointer';
            directBuyBtn.removeAttribute('aria-disabled');
          } else {
            directBuyBtn.style.opacity = '0.5';
            directBuyBtn.style.pointerEvents = 'none';
            directBuyBtn.style.cursor = 'not-allowed';
            directBuyBtn.setAttribute('aria-disabled', 'true');
          }
        }

        if (btn.dataset.sku) {
          const skuDisplays = pdpRoot.querySelectorAll('#pdpSku, #pdpSpecSku, [data-pdp-sku]');
          skuDisplays.forEach((el) => {
            el.textContent = btn.dataset.sku;
          });
        }

        // Synchronize Stepper display for selected size button
        if (window.PlatypussCart && typeof window.PlatypussCart.syncPdpAtcButtons === 'function') {
          window.PlatypussCart.syncPdpAtcButtons();
        }
      });
    });

    // PDP Quantity Stepper (Enforcing max quantity limit, e.g. 50)
    let currentQty = 1;
    const qtyInput = pdpRoot.querySelector('#pdpQuantityInput');
    const qtyMinus = pdpRoot.querySelector('#pdpQtyMinus');
    const qtyPlus = pdpRoot.querySelector('#pdpQtyPlus');
    const maxNotice = pdpRoot.querySelector('#pdpMaxQtyNotice');
    const maxQtyLimit = parseInt(qtyInput?.dataset.max || (window.PlatypussCart && window.PlatypussCart.getMaxQty()) || 50, 10);

    if (qtyMinus && qtyPlus && qtyInput) {
      qtyMinus.addEventListener('click', () => {
        currentQty = Math.max(1, currentQty - 1);
        qtyInput.value = currentQty;
        if (currentQty < maxQtyLimit && maxNotice) {
          maxNotice.style.display = 'none';
        }
      });
      qtyPlus.addEventListener('click', () => {
        if (currentQty >= maxQtyLimit) {
          if (maxNotice) maxNotice.style.display = 'block';
          alert(`Maximum quantity reached (${maxQtyLimit} units per item).`);
          return;
        }
        currentQty++;
        qtyInput.value = currentQty;
        if (currentQty >= maxQtyLimit && maxNotice) {
          maxNotice.style.display = 'block';
        }
      });
    }

    // PDP Add To Cart
    if (atcBtn) {
      atcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = hiddenVariantInput ? hiddenVariantInput.value : null;
        if (!variantId) {
          alert('Please select an available size / option');
          return;
        }

        atcBtn.disabled = true;
        const btnTextEl = atcBtn.querySelector('.btn-text');
        const originalText = btnTextEl ? btnTextEl.textContent : 'ADD TO CART';
        if (btnTextEl) btnTextEl.textContent = 'ADDING...';

        const addQty = currentQty || 1;
        window.PlatypussCart.changeVariantQuantity(variantId, addQty, () => {
          atcBtn.disabled = false;
          if (btnTextEl) btnTextEl.textContent = originalText;
        });
      });
    }

    // PDP Main ATC Stepper Controls
    const pdpStepperMinus = pdpRoot.querySelector('#pdpAtcStepperMinus');
    const pdpStepperPlus = pdpRoot.querySelector('#pdpAtcStepperPlus');

    if (pdpStepperPlus) {
      pdpStepperPlus.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = hiddenVariantInput ? hiddenVariantInput.value : null;
        if (variantId && window.PlatypussCart) {
          pdpStepperPlus.disabled = true;
          window.PlatypussCart.changeVariantQuantity(variantId, 1, () => {
            pdpStepperPlus.disabled = false;
          });
        }
      });
    }

    if (pdpStepperMinus) {
      pdpStepperMinus.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = hiddenVariantInput ? hiddenVariantInput.value : null;
        if (variantId && window.PlatypussCart) {
          pdpStepperMinus.disabled = true;
          window.PlatypussCart.changeVariantQuantity(variantId, -1, () => {
            pdpStepperMinus.disabled = false;
          });
        }
      });
    }

    // Mobile Sticky ATC Button & Stepper Controls
    const stickyAtcBtn = document.getElementById('pdpStickyAddToCartBtn');
    const stickyStepperMinus = document.getElementById('pdpStickyStepperMinus');
    const stickyStepperPlus = document.getElementById('pdpStickyStepperPlus');

    if (stickyAtcBtn) {
      stickyAtcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = hiddenVariantInput ? hiddenVariantInput.value : null;
        if (!variantId) {
          alert('Please select an available size / option');
          return;
        }
        stickyAtcBtn.disabled = true;
        window.PlatypussCart.changeVariantQuantity(variantId, 1, () => {
          stickyAtcBtn.disabled = false;
        });
      });
    }

    if (stickyStepperPlus) {
      stickyStepperPlus.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = hiddenVariantInput ? hiddenVariantInput.value : null;
        if (variantId && window.PlatypussCart) {
          stickyStepperPlus.disabled = true;
          window.PlatypussCart.changeVariantQuantity(variantId, 1, () => {
            stickyStepperPlus.disabled = false;
          });
        }
      });
    }

    if (stickyStepperMinus) {
      stickyStepperMinus.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = hiddenVariantInput ? hiddenVariantInput.value : null;
        if (variantId && window.PlatypussCart) {
          stickyStepperMinus.disabled = true;
          window.PlatypussCart.changeVariantQuantity(variantId, -1, () => {
            stickyStepperMinus.disabled = false;
          });
        }
      });
    }

    // Initial PDP Stepper State Sync
    if (window.PlatypussCart && typeof window.PlatypussCart.syncPdpAtcButtons === 'function') {
      window.PlatypussCart.syncPdpAtcButtons();
    }
  }

  // Global Wishlist Controller
  function initWishlist() {
    function getWishlist() {
      try {
        return JSON.parse(localStorage.getItem('platypuss_wishlist') || '[]');
      } catch (e) {
        return [];
      }
    }

    function saveWishlist(list) {
      try {
        localStorage.setItem('platypuss_wishlist', JSON.stringify(list));
      } catch (e) {}
    }

    function updateWishlistUI() {
      const list = getWishlist();
      const countBadges = document.querySelectorAll('[data-header-wishlist-count], [data-dock-wishlist-count]');
      countBadges.forEach((badge) => {
        const prev = badge.textContent.trim();
        badge.textContent = list.length;
        badge.style.display = list.length > 0 ? '' : 'none';
        if (prev !== String(list.length)) {
          badge.classList.remove('pop');
          void badge.offsetWidth;
          badge.classList.add('pop');
        }
      });

      const drawerCount = document.getElementById('platypussWishlistDrawerCount');
      if (drawerCount) drawerCount.textContent = list.length;

      const toggleBtns = document.querySelectorAll('[data-wishlist-toggle]');
      toggleBtns.forEach((btn) => {
        const handle = btn.dataset.productHandle;
        const icon = btn.querySelector('.material-symbols-outlined');
        if (list.includes(handle)) {
          btn.style.color = 'var(--platypuss-primary, #C6FF00)';
          if (icon) icon.style.fontVariationSettings = "'FILL' 1";
        } else {
          btn.style.color = '';
          if (icon) icon.style.fontVariationSettings = "'FILL' 0";
        }
      });

      window.dispatchEvent(new CustomEvent('platypuss:wishlist-updated', { detail: { count: list.length } }));
    }

    // Wishlist Drawer Controls
    const drawer = document.getElementById('platypussWishlistDrawer');
    const overlay = document.getElementById('platypussWishlistOverlay');
    const closeBtn = document.getElementById('platypussWishlistCloseBtn');
    const clearBtn = document.getElementById('platypussWishlistClearBtn');
    const bodyEl = document.getElementById('platypussWishlistDrawerBody');

    function openWishlistDrawer() {
      if (!drawer || !overlay) return;
      overlay.style.display = 'block';
      setTimeout(() => {
        overlay.classList.add('active');
        drawer.style.transform = 'translateX(0)';
      }, 10);
      document.body.classList.add('platypuss-wishlist-open');
      document.body.style.overflow = 'hidden';
      renderWishlistDrawer();
    }

    function closeWishlistDrawer() {
      if (!drawer || !overlay) return;
      drawer.style.transform = 'translateX(100%)';
      overlay.classList.remove('active');
      document.body.classList.remove('platypuss-wishlist-open');
      setTimeout(() => {
        overlay.style.display = 'none';
        if (!window.PlatypussCart || !window.PlatypussCart.isOpen || !window.PlatypussCart.isOpen()) {
          document.body.style.overflow = '';
        }
      }, 300);
    }

    async function renderWishlistDrawer() {
      if (!bodyEl) return;
      const list = getWishlist();
      if (!list.length) {
        bodyEl.innerHTML = `
          <div style="text-align: center; padding: 60px 20px;">
            <div style="width: 56px; height: 56px; border-radius: 50%; background: var(--platypuss-surface-container); border: 1px solid var(--platypuss-border); display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; color: var(--platypuss-text-muted);">
              <span class="material-symbols-outlined" style="font-size: 28px;">favorite_border</span>
            </div>
            <h3 style="font-family: var(--font-display); font-size: 18px; font-weight: 800; color: var(--platypuss-tertiary); margin: 0 0 8px; text-transform: uppercase;">
              YOUR WANTED LIST IS EMPTY
            </h3>
            <p style="font-size: 13px; color: var(--platypuss-text-muted); margin: 0 0 20px; line-height: 1.5;">
              Tap the heart icon on any product to save it to your local vault.
            </p>
            <a href="/collections/all" class="platypuss-btn-secondary" style="display: inline-flex; font-size: 12px; padding: 10px 18px;" onclick="window.PlatypussTheme && window.PlatypussTheme.closeWishlist && window.PlatypussTheme.closeWishlist();">
              EXPLORE DROPS
            </a>
          </div>
        `;
        return;
      }

      bodyEl.innerHTML = `<div style="text-align: center; padding: 40px; color: var(--platypuss-primary);"><span class="material-symbols-outlined" style="font-size: 28px; animation: spin 1s linear infinite; display: inline-block;">sync</span><p style="font-size: 12px; font-family: var(--font-accent); margin-top: 8px;">LOADING BOUNTIES...</p></div>`;

      try {
        const productPromises = list.map(handle => fetch(`/products/${handle}.js`).then(r => r.ok ? r.json() : null).catch(() => null));
        const products = (await Promise.all(productPromises)).filter(Boolean);

        if (!products.length) {
          bodyEl.innerHTML = `<p style="text-align:center; padding: 40px; color: var(--platypuss-text-muted);">NO SAVED ITEMS FOUND</p>`;
          return;
        }

        let html = '<div style="display: flex; flex-direction: column; gap: 14px;">';
        products.forEach(p => {
          const firstVariant = (p.variants && p.variants.find(v => v.available)) || (p.variants && p.variants[0]);
          const imgUrl = p.featured_image || (p.images && p.images[0]) || '';
          const isAvailable = firstVariant && firstVariant.available;
          const variantTitle = (firstVariant && firstVariant.title && firstVariant.title !== 'Default Title') ? ` • ${firstVariant.title}` : '';
          const priceFormatted = firstVariant ? formatMoney(firstVariant.price) : '';
          const safeTitle = (p.title || '').replace(/"/g, '&quot;');
          const safeHandle = (p.handle || '').replace(/'/g, "\\'");
          html += `
            <div style="display: flex; gap: 14px; background: var(--platypuss-surface-container); border: 1px solid var(--platypuss-border); border-radius: var(--radius-sm); padding: 14px; align-items: center;">
              <a href="${p.url}" style="width: 76px; height: 92px; flex-shrink: 0; border-radius: var(--radius-sm); overflow: hidden; background: #000; border: 1px solid var(--platypuss-border);">
                <img src="${imgUrl}" alt="${safeTitle}" style="width: 100%; height: 100%; object-fit: cover;">
              </a>
              <div style="flex: 1; min-width: 0;">
                <a href="${p.url}" style="font-family: var(--font-display); font-size: 16.5px; font-weight: 700; color: var(--platypuss-tertiary); text-decoration: none; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; line-height: 1.25; margin-bottom: 5px;">
                  ${safeTitle}
                </a>
                <div style="font-family: var(--font-display); font-size: 17px; font-weight: 800; color: var(--platypuss-primary); margin: 2px 0 8px; letter-spacing: 0.02em;">
                  ${priceFormatted}<span style="font-size: 11px; color: var(--platypuss-text-muted); font-family: var(--font-mono); font-weight: 600; margin-left: 6px;">${variantTitle}</span>
                </div>
                <div style="display: flex; gap: 8px;">
                  ${isAvailable ? `
                    ${(window.PlatypussCart && window.PlatypussCart.getVariantQty(firstVariant.id) > 0) ? `
                      <div class="platypuss-card-stepper" style="height: 34px; max-width: 130px; display: flex;">
                        <button type="button" class="platypuss-card-stepper-btn minus" style="width: 34px;" onclick="window.PlatypussCart.changeVariantQuantity(${firstVariant.id}, -1, function() { window.PlatypussRenderWishlist && window.PlatypussRenderWishlist(); })" aria-label="Decrease quantity">
                          <span class="material-symbols-outlined minus-icon" style="font-size: 14px;">${window.PlatypussCart.getVariantQty(firstVariant.id) === 1 ? 'delete' : 'remove'}</span>
                        </button>
                        <div class="platypuss-card-stepper-center">
                          <span class="platypuss-card-stepper-num" style="font-size: 14px;">${window.PlatypussCart.getVariantQty(firstVariant.id)}</span>
                        </div>
                        <button type="button" class="platypuss-card-stepper-btn plus" style="width: 34px;" onclick="window.PlatypussCart.changeVariantQuantity(${firstVariant.id}, 1, function() { window.PlatypussRenderWishlist && window.PlatypussRenderWishlist(); })" aria-label="Increase quantity">
                          <span class="material-symbols-outlined" style="font-size: 14px;">add</span>
                        </button>
                      </div>
                    ` : `
                      <button type="button" class="platypuss-btn-primary" style="padding: 7px 14px; font-size: 12px; font-weight: 800; letter-spacing: 0.03em;" onclick="window.PlatypussTheme.quickAddVariant(${firstVariant.id}, this)">
                        + ADD TO CART
                      </button>
                    `}
                  ` : `
                    <button type="button" class="platypuss-btn-secondary" style="padding: 7px 14px; font-size: 12px; font-weight: 800; letter-spacing: 0.03em; opacity: 0.5; cursor: not-allowed;" disabled>
                      SOLD OUT
                    </button>
                  `}
                  <button type="button" style="background: none; border: 1px solid var(--platypuss-border); border-radius: 4px; color: var(--platypuss-text-muted); padding: 6px 10px; cursor: pointer; font-size: 13px;" onclick="window.PlatypussWishlistRemove('${safeHandle}')" title="Remove">
                    ✕
                  </button>
                </div>
              </div>
            </div>
          `;
        });
        html += '</div>';
        bodyEl.innerHTML = html;
      } catch (err) {
        bodyEl.innerHTML = `<p style="text-align:center; padding: 40px; color: var(--platypuss-error);">FAILED TO LOAD WISHLIST</p>`;
      }
    }

    window.PlatypussRenderWishlist = renderWishlistDrawer;

    // Global Theme & Wishlist API
    window.PlatypussTheme = window.PlatypussTheme || {};
    window.PlatypussTheme.closeWishlist = closeWishlistDrawer;
    window.PlatypussTheme.openWishlist = openWishlistDrawer;

    window.PlatypussTheme.quickAddVariant = function(variantId, btn) {
      if (!variantId) {
        alert('Product variant not available.');
        return;
      }
      const originalHtml = btn ? btn.innerHTML : '+ ADD TO CART';
      if (btn) {
        btn.innerHTML = '<span class="material-symbols-outlined" style="font-size: 14px; animation: spin 1s linear infinite; display: inline-block;">sync</span> ADDING...';
        btn.disabled = true;
      }

      if (window.PlatypussCart && typeof window.PlatypussCart.changeVariantQuantity === 'function') {
        window.PlatypussCart.changeVariantQuantity(variantId, 1, function(cart) {
          if (btn) {
            btn.innerHTML = originalHtml;
            btn.disabled = false;
          }
          if (typeof window.PlatypussRenderWishlist === 'function') {
            window.PlatypussRenderWishlist();
          }
        });
      }
    };

    window.PlatypussWishlistRemove = function(handle) {
      let list = getWishlist();
      list = list.filter(h => h !== handle);
      saveWishlist(list);
      updateWishlistUI();
      renderWishlistDrawer();
    };

    if (closeBtn) closeBtn.addEventListener('click', closeWishlistDrawer);
    if (overlay) overlay.addEventListener('click', closeWishlistDrawer);
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        saveWishlist([]);
        updateWishlistUI();
        renderWishlistDrawer();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.body.classList.contains('platypuss-wishlist-open')) {
        closeWishlistDrawer();
      }
    });

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-wishlist-drawer-trigger]');
      if (trigger) {
        e.preventDefault();
        openWishlistDrawer();
      }

      const btn = e.target.closest('[data-wishlist-toggle]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        const handle = btn.dataset.productHandle;
        if (!handle) return;

        let list = getWishlist();
        let wasAdded = false;
        if (list.includes(handle)) {
          list = list.filter((h) => h !== handle);
          wasAdded = false;
        } else {
          list.push(handle);
          wasAdded = true;
        }
        saveWishlist(list);
        updateWishlistUI();

        if (window.PlatypussToast) {
          if (wasAdded) {
            window.PlatypussToast.show('PIECE SAVED TO WANTED', 'favorite', 'VIEW WANTED', () => {
              openWishlistDrawer();
            });
          } else {
            window.PlatypussToast.show('REMOVED FROM WANTED', 'favorite_border');
          }
        }
      }
    });

    updateWishlistUI();
  }

  // Product Card Click Navigation (tapping or clicking anywhere on the card navigates to PDP)
  function initProductCardClick() {
    document.addEventListener('click', (e) => {
      const card = e.target.closest('[data-product-card]');
      if (!card) return;
      // Do not navigate if user clicked on button, input, quick add, wishlist toggle, or size pill
      if (e.target.closest('button, input, select, textarea, [data-quick-add-btn], [data-wishlist-toggle], .platypuss-size-pill')) {
        return;
      }
      // If user clicked directly on an anchor tag, browser handles navigation
      if (e.target.closest('a')) {
        return;
      }
      // Otherwise navigate to the product page
      const link = card.querySelector('.platypuss-card-media-link') || card.querySelector('.platypuss-card-title a');
      if (link && link.href) {
        window.location.href = link.href;
      }
    });
  }

  // Newsletter AJAX submission & Instant VIP code reveal
  function initNewsletter() {
    const form = document.getElementById('platypussNewsletterForm');
    const formView = document.getElementById('newsletterFormView');
    const successView = document.getElementById('newsletterSuccessView');
    const submitBtn = document.getElementById('newsletterSubmitBtn');
    const btnText = document.getElementById('newsletterBtnText');
    const errorDiv = document.getElementById('newsletterClientError');
    const copyBtn = document.getElementById('newsletterCopyBtn');
    const resetBtn = document.getElementById('newsletterResetBtn');

    // Clipboard copy handler
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        const code = copyBtn.dataset.code || getPrimaryDiscountCode();
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(() => {
            const originalText = copyBtn.textContent;
            copyBtn.textContent = 'COPIED! ✓';
            copyBtn.style.background = '#ffffff';
            copyBtn.style.color = '#121509';
            if (window.PlatypussToast) {
              window.PlatypussToast.show(`VIP CODE ${code} COPIED`, 'content_copy');
            }
            setTimeout(() => {
              copyBtn.textContent = originalText;
              copyBtn.style.background = '';
              copyBtn.style.color = '';
            }, 2500);
          }).catch(() => {
            prompt('Copy VIP code:', code);
          });
        } else {
          prompt('Copy VIP code:', code);
        }
      });
    }

    // Reset button to show form again
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        if (formView && successView) {
          successView.style.display = 'none';
          formView.style.display = 'block';
          if (submitBtn) {
            submitBtn.disabled = false;
            const pct = (window.PLATYPUSS_CONFIG && window.PLATYPUSS_CONFIG.primaryDiscountPercent) || 10;
            if (btnText) btnText.textContent = `UNLOCK ${pct}% OFF →`;
          }
          if (form) form.reset();
        }
      });
    }

    if (!form) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();

      const emailInput = form.querySelector('input[type="email"]');
      if (!emailInput || !emailInput.value.trim() || !emailInput.value.includes('@')) {
        if (errorDiv) {
          errorDiv.textContent = 'Please enter a valid email address.';
          errorDiv.style.display = 'block';
        }
        return;
      }

      if (errorDiv) {
        errorDiv.textContent = '';
        errorDiv.style.display = 'none';
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        if (btnText) btnText.textContent = 'AUTHENTICATING...';
      }

      const formData = new FormData(form);

      fetch(form.action || '/contact#contact_form', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'text/html'
        }
      })
      .then(() => {
        if (formView && successView) {
          formView.style.display = 'none';
          successView.style.display = 'block';
        }
      })
      .catch((err) => {
        console.warn('Newsletter notice:', err);
        if (formView && successView) {
          formView.style.display = 'none';
          successView.style.display = 'block';
        }
      });
    });
  }

  // Global Accordion Controller (FAQ, PDP specs, Contact)
  function initAccordions() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-accordion-trigger]');
      if (!trigger) return;
      e.preventDefault();
      const item = trigger.closest('.platypuss-accordion-item');
      if (item) {
        const isActive = item.classList.contains('active');
        item.classList.toggle('active', !isActive);
        trigger.setAttribute('aria-expanded', !isActive ? 'true' : 'false');
      }
    });
  }

  // Global Promo / Discount Code Copy Button Handler
  function initDiscountCopyButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-discount-copy]');
      if (!btn) return;
      e.preventDefault();
      const code = btn.dataset.discountCopy || getPrimaryDiscountCode();
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(() => {
          const original = btn.textContent;
          btn.textContent = 'COPIED! ✓';
          btn.style.background = '#ffffff';
          btn.style.color = '#0b0b0d';
          if (window.PlatypussToast) {
            window.PlatypussToast.show(`VIP CODE ${code} COPIED`, 'content_copy');
          }
          setTimeout(() => {
            btn.textContent = original;
            btn.style.background = '';
            btn.style.color = '';
          }, 2500);
        }).catch(() => {
          prompt('Copy discount code:', code);
        });
      } else {
        prompt('Copy discount code:', code);
      }
    });
  }

  // Motion-Primitives: Interactive Cursor Spotlight with rAF Throttling
  function initSpotlights() {
    if (!window.matchMedia('(hover: hover)').matches) return;

    let activeTarget = null;
    let targetRect = null;
    let rafId = null;
    let lastX = 0;
    let lastY = 0;

    function update() {
      if (activeTarget && targetRect) {
        const x = lastX - targetRect.left;
        const y = lastY - targetRect.top;
        activeTarget.style.setProperty('--spotlight-x', `${x}px`);
        activeTarget.style.setProperty('--spotlight-y', `${y}px`);
      }
      rafId = null;
    }

    document.addEventListener('mousemove', (e) => {
      const target = e.target.closest('.platypuss-spotlight');
      if (!target) {
        activeTarget = null;
        targetRect = null;
        return;
      }

      if (target !== activeTarget) {
        activeTarget = target;
        targetRect = target.getBoundingClientRect();
      }

      lastX = e.clientX;
      lastY = e.clientY;

      if (!rafId) {
        rafId = requestAnimationFrame(update);
      }
    }, { passive: true });

    window.addEventListener('scroll', () => {
      if (activeTarget) {
        targetRect = activeTarget.getBoundingClientRect();
      }
    }, { passive: true });
  }

  // Motion-Primitives: Scroll Reveal Controller
  function initScrollReveal() {
    const reveals = document.querySelectorAll('[data-reveal]');
    if (!reveals.length) return;

    if (!('IntersectionObserver' in window)) {
      reveals.forEach(el => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });

    reveals.forEach(el => observer.observe(el));
  }

  // Motion-Primitives: Magnetic Micro-Interactions with Spring Cleanup
  function initMagneticButtons() {
    if (!window.matchMedia('(pointer: fine)').matches) return;

    // Restrict magnetic pull strictly to hero primary CTA (preventing jitter across 16-card grids)
    const magneticElements = document.querySelectorAll('.hero-cta-group .platypuss-btn-primary');
    magneticElements.forEach((el) => {
      let isHovered = false;
      let cleanupTimer = null;

      el.addEventListener('mouseenter', () => {
        isHovered = true;
        clearTimeout(cleanupTimer);
        el.style.transition = 'transform 0.15s ease-out';
      });

      el.addEventListener('mousemove', (e) => {
        if (!isHovered) return;
        const rect = el.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const deltaX = (e.clientX - centerX) * 0.08;
        const deltaY = (e.clientY - centerY) * 0.08;
        el.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0)`;
      });

      el.addEventListener('mouseleave', () => {
        isHovered = false;
        el.style.transition = 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
        el.style.transform = 'translate3d(0, 0, 0)';
        // Reset inline style properties so CSS :hover and :active are never permanently overridden
        cleanupTimer = setTimeout(() => {
          if (!isHovered) {
            el.style.transform = '';
            el.style.transition = '';
          }
        }, 460);
      });
    });
  }

  // 3D Canvas Engine: Procedural Particle Vortex & Atmospheric Field
  function initHeroParticleVortex() {
    const canvas = document.getElementById('platypussHeroCanvas');
    if (!canvas) return;

    const heroSection = canvas.closest('.platypuss-hero') || canvas.parentElement;
    if (!heroSection) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let width = 0;
    let height = 0;
    let centerX = 0;
    let centerY = 0;

    const PARTICLE_COUNT = window.innerWidth < 768 ? 90 : 160;
    const particles = [];
    const FOV = 340;

    function resize() {
      const rect = heroSection.getBoundingClientRect();
      width = rect.width || window.innerWidth;
      height = Math.max(rect.height, 480);
      centerX = width / 2;
      centerY = height / 2;

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    }

    const COLOR_TYPES = [
      { r: 198, g: 255, b: 0, volt: true },
      { r: 198, g: 255, b: 0, volt: true },
      { r: 245, g: 245, b: 240, volt: false },
      { r: 255, g: 255, b: 255, volt: false },
      { r: 56, g: 239, b: 125, volt: true }
    ];

    function createParticle() {
      const color = COLOR_TYPES[Math.floor(Math.random() * COLOR_TYPES.length)];
      return {
        x: (Math.random() - 0.5) * (width || 1200) * 1.3,
        y: (Math.random() - 0.5) * (height || 600) * 1.3,
        z: (Math.random() - 0.5) * 420,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.3 - 0.08,
        vz: (Math.random() - 0.5) * 0.35,
        baseRadius: Math.random() * 1.8 + 0.8,
        color: color,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.02 + 0.01,
        rx: 0,
        ry: 0,
        rz: 0,
        projX: 0,
        projY: 0,
        scale: 1,
        alpha: 0,
        visible: false
      };
    }

    resize();
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push(createParticle());
    }

    let targetMouseX = 0;
    let targetMouseY = 0;
    let curMouseX = 0;
    let curMouseY = 0;

    window.addEventListener('mousemove', (e) => {
      const rect = heroSection.getBoundingClientRect();
      if (
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom &&
        e.clientX >= rect.left &&
        e.clientX <= rect.right
      ) {
        targetMouseX = ((e.clientX - rect.left) / width) * 2 - 1;
        targetMouseY = ((e.clientY - rect.top) / height) * 2 - 1;
      } else {
        targetMouseX = 0;
        targetMouseY = 0;
      }
    }, { passive: true });

    let lastScrollY = window.scrollY;
    let scrollVelocity = 0;

    window.addEventListener('scroll', () => {
      const curScroll = window.scrollY;
      scrollVelocity = (curScroll - lastScrollY) * 0.08;
      lastScrollY = curScroll;
    }, { passive: true });

    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('load', resize, { passive: true });
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => resize()).observe(heroSection);
    }

    let animId = null;
    let isIntersecting = true;
    let rotY = 0;
    let rotX = 0;

    function render() {
      curMouseX += (targetMouseX - curMouseX) * 0.05;
      curMouseY += (targetMouseY - curMouseY) * 0.05;
      scrollVelocity *= 0.92;

      rotY += 0.001 + curMouseX * 0.002;
      rotX = curMouseY * 0.1;

      const cosY = Math.cos(rotY);
      const sinY = Math.sin(rotY);
      const cosX = Math.cos(rotX);
      const sinX = Math.sin(rotX);

      ctx.clearRect(0, 0, width, height);

      const boundX = width * 0.68;
      const boundY = height * 0.68;
      const boundZ = 210;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        p.x += p.vx;
        p.y += p.vy - scrollVelocity;
        p.z += p.vz;
        p.pulse += p.pulseSpeed;

        if (p.x > boundX) p.x = -boundX;
        else if (p.x < -boundX) p.x = boundX;
        if (p.y > boundY) p.y = -boundY;
        else if (p.y < -boundY) p.y = boundY;
        if (p.z > boundZ) p.z = -boundZ;
        else if (p.z < -boundZ) p.z = boundZ;

        // True 3D coordinate transformation
        const rx = p.x * cosY - p.z * sinY;
        const rz = p.x * sinY + p.z * cosY;

        const ry = p.y * cosX - rz * sinX;
        const finalZ = p.y * sinX + rz * cosX;

        p.rx = rx;
        p.ry = ry;
        p.rz = finalZ;

        const depth = finalZ + FOV;
        if (depth <= 20) {
          p.visible = false;
          continue;
        }

        const scale = FOV / depth;
        p.scale = scale;
        p.projX = centerX + rx * scale;
        p.projY = centerY + ry * scale;
        p.visible = p.projX >= -24 && p.projX <= width + 24 && p.projY >= -24 && p.projY <= height + 24;

        if (!p.visible) continue;

        // Boundary edge-fading: prevents sudden particle pop-in/pop-out
        const edgeFadeX = Math.min(1, (boundX - Math.abs(p.x)) / (boundX * 0.22));
        const edgeFadeY = Math.min(1, (boundY - Math.abs(p.y)) / (boundY * 0.22));
        const edgeFadeZ = Math.min(1, (boundZ - Math.abs(p.z)) / (boundZ * 0.22));
        const edgeFade = Math.max(0, edgeFadeX * edgeFadeY * edgeFadeZ);

        const baseAlpha = Math.max(0.06, Math.min(0.85, (finalZ + boundZ) / (boundZ * 2)));
        const pulseAlpha = baseAlpha * (0.8 + Math.sin(p.pulse) * 0.2) * edgeFade;
        p.alpha = pulseAlpha;

        if (pulseAlpha <= 0.01) continue;

        const radius = Math.max(0.6, p.baseRadius * scale);

        ctx.beginPath();
        ctx.arc(p.projX, p.projY, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${pulseAlpha.toFixed(3)})`;
        ctx.fill();

        // Subtle specular glow for high-scale volt particles
        if (p.color.volt && scale > 1.08 && pulseAlpha > 0.3) {
          ctx.beginPath();
          ctx.arc(p.projX, p.projY, radius * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(198, 255, 0, ${(pulseAlpha * 0.16).toFixed(3)})`;
          ctx.fill();
        }
      }

      // Constellation Filaments: computed in true 3D Euclidean space with single-pass batched stroke
      const MAX_3D_DIST = 82;
      const MAX_3D_DIST_SQ = MAX_3D_DIST * MAX_3D_DIST;

      ctx.beginPath();
      let drawnLines = 0;
      for (let i = 0; i < particles.length && drawnLines < 120; i++) {
        const p1 = particles[i];
        if (!p1.visible || p1.alpha < 0.15) continue;

        for (let j = i + 1; j < particles.length && drawnLines < 120; j++) {
          const p2 = particles[j];
          if (!p2.visible || p2.alpha < 0.15) continue;

          const dx = p1.rx - p2.rx;
          const dy = p1.ry - p2.ry;
          const dz = p1.rz - p2.rz;
          const distSq = dx * dx + dy * dy + dz * dz;

          if (distSq < MAX_3D_DIST_SQ) {
            ctx.moveTo(p1.projX, p1.projY);
            ctx.lineTo(p2.projX, p2.projY);
            drawnLines++;
          }
        }
      }
      if (drawnLines > 0) {
        ctx.strokeStyle = 'rgba(198, 255, 0, 0.11)';
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }

      if (isIntersecting && !prefersReducedMotion) {
        animId = requestAnimationFrame(render);
      }
    }

    if (prefersReducedMotion) {
      render();
      return;
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          isIntersecting = entry.isIntersecting;
          if (isIntersecting && !animId) {
            animId = requestAnimationFrame(render);
          } else if (!isIntersecting && animId) {
            cancelAnimationFrame(animId);
            animId = null;
          }
        });
      }, { threshold: 0.05, rootMargin: '100px' });
      observer.observe(heroSection);
    } else {
      animId = requestAnimationFrame(render);
    }
  }

  // Motion-Primitives: Interactive 3D Perspective Card Tilt with Specular Glare
  function init3DCardTilt() {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const cards = document.querySelectorAll('.platypuss-card, .hero-card-featured, .hero-card-sub');
    const MAX_TILT = 6.0;

    cards.forEach((card) => {
      let glare = card.querySelector('.platypuss-card-glare');
      if (!glare) {
        glare = document.createElement('div');
        glare.className = 'platypuss-card-glare';
        card.appendChild(glare);
      }

      let isHovered = false;
      let cardRect = null;
      let rafId = null;
      let clientX = 0;
      let clientY = 0;
      let resetTimer = null;

      function updateTilt() {
        if (!isHovered || !cardRect) return;

        const centerX = cardRect.left + cardRect.width / 2;
        const centerY = cardRect.top + cardRect.height / 2;
        const normX = Math.max(-1, Math.min(1, (clientX - centerX) / (cardRect.width / 2)));
        const normY = Math.max(-1, Math.min(1, (clientY - centerY) / (cardRect.height / 2)));

        const rotX = -normY * MAX_TILT;
        const rotY = normX * MAX_TILT;

        const glareX = clientX - cardRect.left;
        const glareY = clientY - cardRect.top;

        card.style.transform = `perspective(1000px) rotateX(${rotX.toFixed(2)}deg) rotateY(${rotY.toFixed(2)}deg) translateY(-5px) scale3d(1.012, 1.012, 1.012)`;
        card.style.setProperty('--card-glare-x', `${glareX}px`);
        card.style.setProperty('--card-glare-y', `${glareY}px`);
        card.style.setProperty('--card-glare-opacity', '0.65');

        rafId = null;
      }

      card.addEventListener('mouseenter', () => {
        isHovered = true;
        clearTimeout(resetTimer);
        cardRect = card.getBoundingClientRect();
        card.style.transition = 'none';
      });

      card.addEventListener('mousemove', (e) => {
        if (!isHovered) return;
        clientX = e.clientX;
        clientY = e.clientY;
        if (!rafId) {
          rafId = requestAnimationFrame(updateTilt);
        }
      }, { passive: true });

      card.addEventListener('mouseleave', () => {
        isHovered = false;
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        card.style.transition = 'transform 0.55s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.55s cubic-bezier(0.16, 1, 0.3, 1)';
        card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0) scale3d(1, 1, 1)';
        card.style.setProperty('--card-glare-opacity', '0');

        resetTimer = setTimeout(() => {
          if (!isHovered) {
            card.style.transform = '';
            card.style.transition = '';
            card.style.boxShadow = '';
          }
        }, 580);
      });
    });
  }

  // Motion-Primitives: Mascot Concentric Disc Multi-Layer 3D Parallax
  function initMascot3DParallax() {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const stages = document.querySelectorAll('[data-mascot-stage]');
    if (!stages.length) return;

    stages.forEach((stage) => {
      const container = stage.closest('.platypuss-philosophy') || stage.parentElement;
      if (!container) return;

      const halo = stage.querySelector('.mascot-parallax-halo');
      const discOuter = stage.querySelector('.mascot-parallax-disc-outer');
      const discInner = stage.querySelector('.mascot-parallax-disc-inner');
      const graphic = stage.querySelector('.mascot-parallax-graphic');
      const badge1 = stage.querySelector('.mascot-parallax-badge-1');
      const badge2 = stage.querySelector('.mascot-parallax-badge-2');

      let isHovered = false;
      let targetX = 0;
      let targetY = 0;
      let curX = 0;
      let curY = 0;
      let animId = null;

      function updateLayers() {
        curX += (targetX - curX) * 0.08;
        curY += (targetY - curY) * 0.08;

        if (halo) {
          halo.style.transform = `translate3d(${(curX * 12).toFixed(1)}px, ${(curY * 12).toFixed(1)}px, 0)`;
        }
        if (discOuter) {
          discOuter.style.transform = `translate3d(${(curX * 20).toFixed(1)}px, ${(curY * 20).toFixed(1)}px, 0)`;
        }
        if (discInner) {
          discInner.style.transform = `translate3d(${(curX * 34).toFixed(1)}px, ${(curY * 34).toFixed(1)}px, 10px)`;
        }
        if (graphic) {
          graphic.style.transform = `translate3d(${(curX * 52).toFixed(1)}px, ${(curY * 52).toFixed(1)}px, 30px) rotateX(${(-curY * 6).toFixed(1)}deg) rotateY(${(curX * 6).toFixed(1)}deg)`;
        }
        if (badge1) {
          badge1.style.transform = `translate3d(${(curX * 72).toFixed(1)}px, ${(curY * 72).toFixed(1)}px, 50px) rotate(${(-3 + curX * 4).toFixed(1)}deg)`;
        }
        if (badge2) {
          badge2.style.transform = `translate3d(${(curX * 82).toFixed(1)}px, ${(curY * 82).toFixed(1)}px, 60px) rotate(${(3 - curX * 4).toFixed(1)}deg)`;
        }

        const delta = Math.abs(targetX - curX) + Math.abs(targetY - curY);
        if (isHovered || delta > 0.002) {
          animId = requestAnimationFrame(updateLayers);
        } else {
          animId = null;
        }
      }

      container.addEventListener('mouseenter', () => {
        isHovered = true;
        if (!animId) animId = requestAnimationFrame(updateLayers);
      });

      container.addEventListener('mousemove', (e) => {
        const rect = stage.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        targetX = Math.max(-1, Math.min(1, (e.clientX - centerX) / (rect.width / 2)));
        targetY = Math.max(-1, Math.min(1, (e.clientY - centerY) / (rect.height / 2)));
        if (!animId) animId = requestAnimationFrame(updateLayers);
      }, { passive: true });

      container.addEventListener('mouseleave', () => {
        isHovered = false;
        targetX = 0;
        targetY = 0;
        if (!animId) animId = requestAnimationFrame(updateLayers);
      });
    });
  }

  // Initialize on DOM Ready
  document.addEventListener('DOMContentLoaded', () => {
    window.PlatypussToast.init();
    window.PlatypussCart.init();
    initProductCardPills();
    initProductCardClick();
    initPDP();
    initWishlist();
    initNewsletter();
    initAccordions();
    initDiscountCopyButtons();
    initSpotlights();
    initScrollReveal();
    initMagneticButtons();
    initHeroParticleVortex();
    init3DCardTilt();
    initMascot3DParallax();
  });
})();
